/* eslint-disable import/no-cycle */
import CryptoJS from 'crypto-js';
import axios from 'axios';
import generateVersionHash from '@utils/generateVersionHash';
import { attempt, createStoreFetcher } from '@/lib/http';
import { btoa, atob, showAlert } from '../../lib/helpers';
import { dataIntervalExport, dataIntervalUpload } from '../params';
import { postApi } from '../../lib/fetcher';
import api from './constants';
import unmaskingData from '../../lib/utils/unmaskingData';

const aggregator = {
  namespaced: true,

  state() {
    return {
      allAggregators: [],
      totalAggregators: 0,
      allMerchants: [],
      totalMerchants: 0,
      loading: false,
      errors: [],
      flashErrorMessage: '',
      flashError: false,
      merchant: null,
      aggregator: null,
      aggregatorDocument: [],
      countData: 0,
      countSuccess: 0,
      countFailed: 0,
      uploadProgress: 0,
      onUpload: false,
      insertProgress: 0,
      onInsert: false,
      insertName: '',
      importId: null,
      hasFailedImport: false,
      failedImportController: '',
      failedImportDate: '',
      countDone: 0,
      countSame: 0,
      intervalImport: null,
      intervalExport: null,
      exportId: null,
      countDataExport: 0,
      totalPage: 0,
      requestData: null,
      loadingStatus: false,
    };
  },

  mutations: {
    setAggregators(state, { payload }) {
      state.allAggregators = payload.data.list;
    },

    setMerchants(state, { payload }) {
      state.allMerchants = payload.data.list;
    },

    clearMerchants(state) {
      state.allMerchants = [];
      state.totalMerchants = 0;
    },

    setTotalMerchants(state, { payload }) {
      state.totalMerchants = parseInt(payload.data.countdata, 10);
      state.totalPage = parseInt(payload.data.totalPage, 10);
    },

    setFlashErrorMessage(state, payload) {
      state.flashErrorMessage = payload;
      state.flashError = payload != '';
    },

    clearAggregators(state) {
      state.allAggregators = [];
      state.totalAggregators = 0;
    },

    clearErrors(state) {
      state.errors = [];
    },

    clearErrorVariable(state, variable) {
      state.errors = { ...state.errors, [variable]: [] };
    },

    setErrors(state, payload) {
      state.errors = payload;
    },

    setTotalAggregators(state, { payload }) {
      state.totalAggregators = parseInt(payload.data.countdata, 10);
      state.totalPage = parseInt(payload.data.totalPage, 10);
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setDetailAggregator(state, { payload }) {
      state.aggregator = payload;
    },

    setAggregatorDocument(state, document) {
      state.aggregatorDocument.push(document);
    },

    setAggregatorVariable(state, { variable, payload }) {
      state.aggregator[variable] = payload;
    },

    setUploadProgress(state, uploadProgress) {
      state.uploadProgress = uploadProgress;
    },
    setOnUpload(state, onUpload) {
      state.onUpload = onUpload;
    },
    setInsertProgress(state, insertProgress) {
      state.insertProgress = insertProgress;
    },
    setInsertName(state, insertName) {
      state.insertName = insertName;
    },
    setOnInsert(state, onInsert) {
      state.onInsert = onInsert;
    },
    setIntervalImport(state, payload) {
      state.intervalImport = payload;
    },
    setImportData(state, payload) {
      state.importId = payload.import_id;
      state.countData = payload.count_data;
    },
    setIntervalExport(state, payload) {
      state.intervalExport = payload;
    },
    setExportData(state, payload) {
      state.exportId = payload.export_id;
      state.countDataExport = payload.count_data;
    },
    setStateVariable(state, { payload, variable }) {
      state[variable] = payload;
    },
    clearLastResult(state) {
      state.countData = 0;
      state.countSuccess = 0;
      state.countFailed = 0;
      state.countDone = 0;
      state.countSame = 0;
      state.uploadProgress = 0;
      state.onUpload = false;
      state.insertProgress = 0;
      state.onInsert = false;
      state.insertName = '';
      state.intervalImport = null;
      state.importId = null;
      state.hasFailedImport = false;
      state.failedImportController = '';
      state.failedImportDate = '';
    },
    setCancelation(state, payload) {
      state.requestData = payload;
    },
    setLoadingStatus(state, loading) {
      state.loadingStatus = loading;
    },
  },

  actions: {
    async validate(ctx, { date, no_rekening }) {
      if (ctx.state.requestData) ctx.state.requestData.cancel();
      const axiosSource = axios.CancelToken.source();
      ctx.commit('setCancelation', {
        cancel: axiosSource.cancel,
        msg: 'Loading...',
      });

      const data = JSON.stringify({
        account_number: no_rekening,
        version: btoa(
          CryptoJS.SHA256(
            no_rekening +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/onboarding/rekening/validate-rekening`;

      const addConfig = { cancelToken: axiosSource.token };

      const responseData = await postApi(ctx, urlApi, data, addConfig);

      let resVal = false;
      if (responseData?.code == 200) {
        resVal = true;
      }
      return resVal;
    },

    async fetchAggregators(
      ctx,
      { date, page, itemsPerPage, sortBy, sortDesc, search }
    ) {
      ctx.commit('clearAggregators');
      ctx.commit('setLoading', true);
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let sort_by = '';
      if (sortBy[0] != null) {
        sort_by = `${sortBy[0]} ${sort}`;
      } else {
        sort_by = `id ${sort}`;
      }

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        version: btoa(
          CryptoJS.SHA256(
            itemsPerPage.toString() +
              page.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/client/inputer-aggregator/index`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData) {
        ctx.commit('setTotalAggregators', {
          payload: responseData,
        });
        ctx.commit('setAggregators', {
          payload: responseData,
        });
      }
    },

    async fetchMerchants(
      ctx,
      {
        date,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        search,
        aggregatorId,
        merchantId,
        action,
      }
    ) {
      ctx.commit('clearMerchants');
      ctx.commit('setLoading', true);
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let sort_by = '';
      if (sortBy[0] != null) {
        sort_by = `${sortBy[0]} ${sort}`;
      } else {
        sort_by = `id ${sort}`;
      }

      const controller = (ctrl) => {
        switch (ctrl) {
          case 'merchant':
            return 'submerchant/inputer-submerchant/index';
          case 'outlet':
            return 'outlet/inputer-outlet/index';
          default:
            return 'merchantsub/inputer-submerchant/index';
        }
      };
      const aggregatorIdVal = aggregatorId == null ? '' : aggregatorId;
      const merchantIdVal = merchantId == null ? '' : merchantId;

      const version = (act) => {
        switch (act) {
          case 'merchant':
            return btoa(
              CryptoJS.SHA256(
                aggregatorIdVal.toString() +
                  sort_by.toString() +
                  itemsPerPage.toString() +
                  page.toString() +
                  date +
                  atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
              ).toString()
            );
          case 'outlet':
            return btoa(
              CryptoJS.SHA256(
                merchantIdVal.toString() +
                  sort_by.toString() +
                  itemsPerPage.toString() +
                  page.toString() +
                  date +
                  atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
              ).toString()
            );
          default:
            return btoa(
              CryptoJS.SHA256(
                itemsPerPage.toString() +
                  page.toString() +
                  date +
                  atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
              ).toString()
            );
        }
      };

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        aggregator_id: aggregatorIdVal.toString(),
        merchant_id: merchantIdVal.toString(),
        version: version(action),
      });

      const urlApi = `/index.php?r=api/v1/${controller(action)}`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData) {
        ctx.commit('setTotalMerchants', {
          payload: responseData,
        });
        ctx.commit('setMerchants', {
          payload: responseData,
        });
      }
    },

    /**
     * Registers a new aggregator.
     *
     * A rejected submission raises an alert on its own, and a field-level
     * rejection additionally fills the errors that the form renders under each
     * input.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {Object} params.payload - Form values collected by the screen.
     * @returns {Promise<Object|false>} The response envelope, or false when the
     *   submission was rejected.
     */
    async createAggregator(ctx, { date, payload }) {
      const data = {
        pic_name: payload.pic_name,
        pic_ktp_no: payload.pic_ktp_no,
        pic_jabatan: payload.pic_jabatan,
        pic_mobile_phone: payload.pic_mobile_phone,
        pic_email: payload.pic_email,
        aggre_company_name: payload.aggre_company_name,
        aggre_owner_name: payload.aggre_owner_name,
        aggre_ktp_no: payload.aggre_ktp_no,
        aggre_npwp_no: payload.aggre_npwp_no,
        aggre_address: payload.aggre_address,
        aggre_email: payload.aggre_email,
        aggre_phone: payload.aggre_phone,
        aggre_phone_area_code: payload.aggre_phone_area_code,
        aggre_mobile_phone: payload.aggre_mobile_phone,
        province_id: '',
        province_name: payload.province_name,
        city_id: '',
        city_name: payload.city_name,
        sub_district_id: '',
        sub_district_name: payload.sub_district_name,
        urban_id: '',
        urban_name: payload.urban_name,
        postal_code: payload.postal_code,
        bcko: payload.bcko,
        cmer: payload.cmer,
        referral_code: payload.referral_code,
        no_rekening: payload.no_rekening,
        approver_1: payload.approver_1,
        approver_2: payload.approver_2,
        endpoint: payload.endpoint,
        ip_address: payload.ip_address,
        qr_type: payload.qr_type,
        notif_sticker_status: payload.notif_sticker_status,
        version: generateVersionHash(payload.no_rekening, date),
      };

      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.CREATE_AGGREGATOR, data);

        showAlert(
          ctx,
          res.message || 'Register Aggregator Success !',
          'mdi-check-circle',
          'green'
        );

        return res;
      }, false);
    },

    /**
     * Saves changes to an existing aggregator.
     *
     * Unique fields are sent only when the user actually changed them, so the
     * backend does not report a value as taken by the very record being edited.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string|number} params.aggregator_id - Record being edited.
     * @param {Object} params.daerah - Selected region names.
     * @param {Object} params.tempUniqueData - Values as loaded, used to detect
     *   which unique fields changed.
     * @param {Object} params.regionalMapping - BCKO, CMER and referral code.
     * @returns {Promise<Object|false>} The response envelope, or false when the
     *   submission was rejected.
     */
    async updateAggregator(
      ctx,
      { date, aggregator_id, daerah, tempUniqueData, regionalMapping }
    ) {
      const data = {
        aggregator_id,
        pic_name: ctx.state.aggregator.aggre_pic_name,
        pic_ktp_no:
          tempUniqueData.aggre_pic_ktp_no ==
          ctx.state.aggregator.aggre_pic_ktp_no
            ? ''
            : ctx.state.aggregator.aggre_pic_ktp_no,
        pic_jabatan: ctx.state.aggregator.aggre_pic_jabatan,
        pic_mobile_phone: ctx.state.aggregator.aggre_pic_mobile_phone,
        pic_email:
          tempUniqueData.aggre_pic_email == ctx.state.aggregator.aggre_pic_email
            ? ''
            : ctx.state.aggregator.aggre_pic_email,
        aggre_company_name: ctx.state.aggregator.company_name,
        aggre_owner_name: ctx.state.aggregator.owner_name,
        aggre_ktp_no:
          tempUniqueData.ktp_no == ctx.state.aggregator.ktp_no
            ? ''
            : ctx.state.aggregator.ktp_no,
        aggre_npwp_no:
          tempUniqueData.npwp_no == ctx.state.aggregator.npwp_no
            ? ''
            : ctx.state.aggregator.npwp_no,
        aggre_address: ctx.state.aggregator.address,
        aggre_email:
          tempUniqueData.email == ctx.state.aggregator.email
            ? ''
            : ctx.state.aggregator.email,
        aggre_phone: ctx.state.aggregator.phone,
        aggre_phone_area_code: ctx.state.aggregator.phone_area_code,
        province_name: daerah.province_name,
        city_name: daerah.city_name,
        sub_district_name: daerah.sub_district_name,
        urban_name: daerah.urban_name,
        postal_code: daerah.postal_code,
        province_id: '',
        city_id: '',
        sub_district_id: '',
        urban_id: '',
        no_rekening:
          tempUniqueData.account_number == ctx.state.aggregator.account_number
            ? ''
            : ctx.state.aggregator.account_number,
        approver_1: ctx.state.aggregator.approver_1_id,
        approver_2: ctx.state.aggregator.approver_2_id,
        bcko: regionalMapping?.bcko || '',
        cmer: regionalMapping?.cmer || '',
        referral_code: regionalMapping?.referral_code || '',
        endpoint: ctx.state.aggregator.endpoint,
        ip_address: ctx.state.aggregator.ip_address,
        notif_sticker_status: ctx.state.aggregator.notif_sticker_status,
        version: generateVersionHash(aggregator_id.toString(), date),
      };

      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.UPDATE_AGGREGATOR, data);

        showAlert(
          ctx,
          res.message || 'Update Aggregator Success !',
          'mdi-check-circle',
          'green'
        );

        return res;
      }, false);
    },

    async viewAggregator(ctx, { date, id, isPageUpdate }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id,
        version: btoa(
          CryptoJS.SHA256(
            id.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const headers = unmaskingData(id, date, isPageUpdate);

      const urlApi = `/index.php?r=api/v1/client/inputer-aggregator/view`;

      const responseData = await postApi(ctx, urlApi, data, headers ? { headers } : {});

      if (responseData?.code == '200') {
        ctx.commit('setDetailAggregator', {
          payload: responseData.data,
        });
      }
    },

    async viewDocument(ctx, { date, id, doc_type, type, download }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        account_id: id,
        type,
        doc_type,
        download,
        version: btoa(
          CryptoJS.SHA256(
            type.toString() +
              doc_type.toString() +
              id.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/document/document/view`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code == '200') {
        return responseData.data.base64_image;
      }
      return null;
    },

    async downloadTemplate(ctx) {
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/onboarding/bulk-sub-merchant/download`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
      };
      return axios(config)
        .then((response) => response)
        .catch(async (error) => {
          let message = 'Failed to download template. Please try again.';
          if (error.response?.data instanceof Blob) {
            try {
              const parsed = JSON.parse(await error.response.data.text());
              message =
                parsed.message || parsed.message_en || parsed.message_id || message;
            } catch (e) {
              // response body is not JSON, keep the default message
            }
          }
          if (error.response?.status === 401) {
            ctx.dispatch('logout', null, { root: true });
          } else {
            showAlert(ctx, message, 'mdi-alert', 'red');
          }
          return null;
        });
    },

    async bulkUploadSub(
      ctx,
      {
        file,
        date,
        action,
        aggregator_id,
        merchant_id,
        type_pten = null,
        type_merchant = null,
      }
    ) {
      const data = new FormData();
      data.append('file', file);
      data.append('aggregator_id', aggregator_id);
      if (type_pten) data.append('type_pten', type_pten);
      if (type_merchant) data.append('type_merchant', type_merchant);
      ctx.commit('setOnUpload', true);
      if (merchant_id) data.append('merchant_id', merchant_id);
      ctx.commit('setOnUpload', true);
      ctx.commit(
        'setInsertProgressGlobal',
        {
          absoluteBar: true,
          absoluteBarText: 'Uploading...',
        },
        { root: true }
      );

      const controller = (ctrl) => {
        switch (ctrl) {
          case 'add-sub':
            return 'bulk-sub-merchant';
          case 'update-sub-pten':
            return 'bulk-update-merchant';
          case 'update-sub-non-pten':
            return 'bulk-update-merchant';
          case 'block-unblock':
            return 'bulk-block-merchant';
          default:
            return ctrl;
        }
      };

      const failed = () => {
        ctx.commit('setOnUpload', false);
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Upload Failed !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
      };

      try {
        const urlApi = `/index.php?r=api/v1/onboarding/${controller(
          action
        )}/import`;

        const addConfig = {
          onUploadProgress(progressEvent) {
            ctx.commit(
              'setUploadProgress',
              parseInt(
                Math.round((progressEvent.loaded / progressEvent.total) * 100),
                10
              )
            );
          },
        };

        const responseData = await postApi(ctx, urlApi, data, addConfig);

        if (responseData?.code == '200') {
          ctx.commit('setImportData', responseData.data);
          const interval = setInterval(() => {
            ctx.dispatch('importStatus', {
              date,
              controller: controller(action),
            });
          }, dataIntervalUpload);
          ctx.commit('setIntervalImport', interval);
          ctx.commit('setInsertName', 'Start to insert data..');
          ctx.commit('setOnInsert', true);
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: true,
              absoluteBarText: 'Start to insert data..',
            },
            { root: true }
          );
        } else {
          failed();
          if (responseData.code == '302') {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: responseData.data.message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          }
        }
        ctx.commit('setOnUpload', false);
        return responseData;
      } catch (error) {
        failed();
        return false;
      }
    },

    async importStatus(ctx, { date, controller }) {
      const data = JSON.stringify({
        import_id: ctx.state.importId,
        count_data: ctx.state.countData,
        version: btoa(
          CryptoJS.SHA256(
            ctx.state.importId.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const failed = () => {
        clearInterval(ctx.state.intervalImport);
        ctx.commit('setOnInsert', false);
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Upload Failed !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
      };

      try {
        const urlApi = `/index.php?r=api/v1/onboarding/${controller}/status-import`;

        const responseData = await postApi(ctx, urlApi, data);

        if (responseData?.code == '00') {
          clearInterval(ctx.state.intervalImport);
          ctx.commit('setOnInsert', false);
          ctx.commit('setStateVariable', {
            variable: 'countSuccess',
            payload: responseData.data.count_success,
          });
          ctx.commit('setStateVariable', {
            variable: 'countFailed',
            payload: responseData.data.count_failed,
          });
          if (responseData.data.count_success != ctx.state.countData) {
            ctx.commit('setStateVariable', {
              variable: 'failedImportController',
              payload: controller,
            });
            ctx.commit('setStateVariable', {
              variable: 'failedImportDate',
              payload: date,
            });
            ctx.commit('setStateVariable', {
              variable: 'hasFailedImport',
              payload: true,
            });
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: 'Upload done with some failed record !',
                icon: 'mdi-alert',
                color: 'orange',
              },
              { root: true }
            );
          } else {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: 'Success, Bulk Upload Done !',
                icon: 'mdi-alert',
                color: 'green',
              },
              { root: true }
            );
          }
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: false,
              absoluteBarText: '',
            },
            { root: true }
          );
        } else if (responseData?.code == 'A0') {
          const totalDataDone =
            parseInt(responseData.data.count_success, 10) +
            parseInt(responseData.data.count_failed, 10);
          ctx.commit(
            'setInsertName',
            `Insert ${totalDataDone}/${ctx.state.countData} data..`
          );
          ctx.commit(
            'setInsertProgress',
            (totalDataDone / ctx.state.countData) * 100
          );
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: true,
              absoluteBarText: `Insert ${totalDataDone}/${ctx.state.countData} data..`,
            },
            { root: true }
          );
        } else {
          failed();
        }
      } catch (error) {
        failed();
      }
    },

    async downloadFailedImport(ctx) {
      const date = ctx.state.failedImportDate;
      const controller = ctx.state.failedImportController;
      const data = JSON.stringify({
        import_id: ctx.state.importId,
        version: btoa(
          CryptoJS.SHA256(
            ctx.state.importId.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/onboarding/${controller}/download-failed-import`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };
      return axios(config)
        .then((response) => response)
        .catch(async (error) => {
          let message = 'Failed to download failed records. Please try again.';
          if (error.response?.data instanceof Blob) {
            try {
              const parsed = JSON.parse(await error.response.data.text());
              message =
                parsed.message || parsed.message_en || parsed.message_id || message;
            } catch (e) {
              // response body is not JSON, keep the default message
            }
          }
          if (error.response?.status === 401) {
            ctx.dispatch('logout', null, { root: true });
          } else {
            showAlert(ctx, message, 'mdi-alert', 'red');
          }
          return null;
        });
    },

    async exportList(
      ctx,
      {
        date,
        page,
        itemsPerPage,
        sortBy,
        sortDesc,
        search,
        view,
        IDAggregator,
        IDMerchant,
      }
    ) {
      if (ctx.rootState.isExport) {
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Export in progress !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        return;
      }
      ctx.commit('setLoading', true);
      ctx.commit('setIsExport', true, { root: true });
      ctx.commit('setStateVariable', {
        payload: 0,
        variable: 'countSameExport',
      });
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let sort_by = '';
      if (sortBy[0] != null) {
        const q = sortBy[0] == 'name' ? 'merchant_name' : sortBy[0];
        sort_by = `${q} ${sort}`;
      } else {
        sort_by = `id ${sort}`;
      }
      const controller = (ctrl) => {
        switch (ctrl) {
          case 'aggregator':
            return 'export-inputer-aggregator';
          case 'sub-merchant':
            return 'export-inputer-sub-merchant';
          case 'sub-merchant-outlet':
            return 'export-inputer-outlet';
          default:
            return 'export-inputer-aggregator';
        }
      };

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        aggregator_id: IDAggregator.toString(),
        merchant_id: IDMerchant.toString(),
        version: btoa(
          CryptoJS.SHA256(
            IDAggregator.toString() +
              IDMerchant.toString() +
              itemsPerPage.toString() +
              page.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const failed = () => {
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Export Failed !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
        ctx.commit('setIsExport', false, { root: true });
      };

      const urlApi = `/index.php?r=api/v1/export/${controller(view)}/index`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code == '200') {
        if (responseData.data.message != null) {
          ctx.commit(
            'setSnackbar',
            {
              status: true,
              message: responseData.data.message,
              icon: 'mdi-alert',
              color: 'red',
            },
            { root: true }
          );
          ctx.commit('setIsExport', false, { root: true });
          return;
        }
        ctx.commit('setExportData', responseData.data);
        const interval = setInterval(() => {
          ctx.dispatch(
            'exportStatus',
            {
              date,
              exportId: ctx.state.exportId,
              countDataExport: ctx.state.countDataExport,
              controller: controller(view),
            },
            { root: true }
          );
        }, dataIntervalExport);
        ctx.commit('setIntervalExport', interval, { root: true });
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: true,
            absoluteBarText: 'Start to export data..',
          },
          { root: true }
        );
      } else {
        failed();
      }
    },
  },
};
export default aggregator;
